import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MenuToggle } from "./MenuToggle";
import { Navigation } from "./Navigation";
import { useNavigate } from "react-router-dom";
import { useSidebar } from "../contexts/SidebarContext";
import { Building2, X } from "lucide-react";

// Sidebar panel animation variants
const sidebarVariants = {
  open: {
    x: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30,
    },
  },
  closed: {
    x: "-100%",
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 40,
    },
  },
};

// Backdrop animation variants
const backdropVariants = {
  open: {
    opacity: 1,
    transition: { duration: 0.3 },
  },
  closed: {
    opacity: 0,
    transition: { duration: 0.3, delay: 0.1 },
  },
};

export const Example = () => {
  const { isOpen, toggleSidebar } = useSidebar();
  const navigate = useNavigate();

  const menuItems = [
    { id: 0, text: "Home", route: "/", onClick: () => navigate("/") },
    { id: 1, text: "About", route: "/about", onClick: () => navigate("/about") },
    { id: 2, text: "Contact", route: "/contact", onClick: () => navigate("/contact") },
  ];

  const closeSidebar = () => {
    if (isOpen) toggleSidebar();
  };

  return (
    <>
      {/* Menu Toggle Button - Always visible */}
      <MenuToggle toggle={toggleSidebar} isOpen={isOpen} />

      {/* Sidebar and Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop Overlay */}
            <motion.div
              key="backdrop"
              variants={backdropVariants}
              initial="closed"
              animate="open"
              exit="closed"
              onClick={closeSidebar}
              className="fixed inset-0 z-40 bg-slate-900/20 backdrop-blur-sm"
            />

            {/* Sidebar Panel */}
            <motion.aside
              key="sidebar"
              variants={sidebarVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="fixed top-0 left-0 bottom-0 z-50 w-72 bg-white shadow-2xl shadow-slate-900/10 flex flex-col"
            >
              {/* Decorative Gradient Line */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500" />

              {/* Header / Brand */}
              <div className="relative px-6 pt-6 pb-4">
                {/* Close Button */}
                <motion.button
                  onClick={closeSidebar}
                  className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <X className="w-5 h-5" />
                </motion.button>

                {/* Logo */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="font-display text-lg font-bold text-slate-900">
                      PropertyBulk
                    </h2>
                    <p className="text-xs text-slate-500">
                      Data Enrichment
                    </p>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="mx-6 h-px bg-gradient-to-r from-slate-200 via-slate-100 to-transparent" />

              {/* Navigation */}
              <div className="flex-1 overflow-y-auto py-4">
                <Navigation menuItems={menuItems} onClose={closeSidebar} />
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-slate-100">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span>All systems operational</span>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Example;
